import { useQuery } from "@tanstack/react-query";

import {
  IIntegrationMessagePayload,
  IIntegrationQueueEntry,
} from "../../../@types/admin/AdminIntegrationQueue";
import { integrationQueueService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { HttpError } from "../../../utils/HttpError";

/** Stable empty list so consumers don't see a new array identity per render. */
const EMPTY_ENTRIES: IIntegrationQueueEntry[] = [];

/**
 * The queue tail — the page's list query.
 *
 * A 404 here is expected, not broken: the endpoints only exist in development
 * and staging, so the page reports "not available in this environment" rather
 * than an error. The status is surfaced for that reason.
 */
export function useIntegrationQueueEntries(processName?: string) {
  const { data, isLoading, isError, error } = useQuery<
    IIntegrationQueueEntry[]
  >({
    queryKey: ["admin-integration-queue", processName ?? "all"],
    queryFn: async () =>
      ensureOk(await integrationQueueService.getQueueEntries(processName)),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return {
    queueEntries: data ?? EMPTY_ENTRIES,
    isLoading,
    isError,
    status: error instanceof HttpError ? error.status : undefined,
  };
}

/**
 * One message's payload. Idle until a row is chosen — the payload is excluded
 * from the list precisely so it isn't fetched 20 times over.
 */
export function useIntegrationMessagePayload(id: string | undefined) {
  const { data, isLoading, isError } = useQuery<IIntegrationMessagePayload>({
    queryKey: ["admin-integration-message", id],
    queryFn: async () =>
      ensureOk(await integrationQueueService.getMessagePayload(id!)),
    enabled: Boolean(id),
  });

  return { messagePayload: data, isLoading, isError };
}
